import React, { useState, useEffect } from "react";
import {
  X,
  Building2,
  Mail,
  Phone,
  FileText,
  User,
  Send,
  AlertCircle,
  CheckCircle,
  Loader,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  createChildOrganizationAPI,
  fetchAccessibleOrgsAPI,
} from "../../actioncreators/actioncreators";

const AddChildOrganization = ({ isOpen, onClose, onSuccess, parentOrgId = null }) => {
  const [formData, setFormData] = useState({
    orgName: "",
    orgType: "",
    description: "",
    email: "",
    mobile: "",
    parentOrgId: parentOrgId || "",
    adminEmail: "",
    adminName: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accessibleOrgs, setAccessibleOrgs] = useState([]);
  const [inviteAdmin, setInviteAdmin] = useState(false);

  const orgTypes = [
    { value: "BRANCH", label: "Branch Office" },
    { value: "REGIONAL", label: "Regional Office" },
    { value: "DIVISION", label: "Division" },
    { value: "SUBSIDIARY", label: "Subsidiary" },
    { value: "FRANCHISE", label: "Franchise" },
    { value: "OTHER", label: "Other" },
  ];

  useEffect(() => {
    if (isOpen) {
      fetchAccessibleOrgs();
    }
  }, [isOpen]);

  const fetchAccessibleOrgs = async () => {
    try {
      const response = await fetchAccessibleOrgsAPI();
      if (response?.businessStatusCode === 2) {
        setAccessibleOrgs(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching accessible orgs:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.orgName.trim()) {
      newErrors.orgName = "Organization name is required";
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (formData.mobile && !/^[0-9]{10}$/.test(formData.mobile)) {
      newErrors.mobile = "Mobile must be 10 digits";
    }

    if (inviteAdmin) {
      if (!formData.adminEmail.trim()) {
        newErrors.adminEmail = "Admin email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
        newErrors.adminEmail = "Invalid admin email format";
      }
      if (!formData.adminName.trim()) {
        newErrors.adminName = "Admin name is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        orgName: formData.orgName.trim(),
        orgType: formData.orgType || "OTHER",
        description: formData.description.trim(),
        email: formData.email.trim(),
        mobile: formData.mobile.trim(),
      };

      if (formData.parentOrgId) {
        payload.parentOrgId = formData.parentOrgId;
      }

      if (inviteAdmin && formData.adminEmail) {
        payload.adminEmail = formData.adminEmail.trim();
        payload.adminName = formData.adminName.trim();
      }

      const response = await createChildOrganizationAPI(payload);

      if (response?.businessStatusCode === 2) {
        onSuccess(response.data);
      } else {
        toast.error(response?.message || "Failed to create organization");
      }
    } catch (error) {
      console.error("Error creating organization:", error);
      toast.error("Failed to create organization");
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "12px 14px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    fontSize: "14px",
    outline: "none",
    transition: "all 0.2s ease",
    backgroundColor: "#fff",
  };

  const inputFocusStyle = {
    borderColor: "#ff7a00",
    boxShadow: "0 0 0 3px rgba(255, 122, 0, 0.1)",
  };

  const labelStyle = {
    display: "block",
    fontSize: "13px",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "6px",
  };

  const errorStyle = {
    fontSize: "12px",
    color: "#dc2626",
    marginTop: "4px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1050,
        animation: "fadeIn 0.2s ease",
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "16px",
          width: "100%",
          maxWidth: "600px",
          maxHeight: "90vh",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
          animation: "slideUp 0.3s ease",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#fff7ed",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "12px",
                backgroundColor: "#ff7a00",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Building2 size={24} color="#fff" />
            </div>
            <div>
              <h2
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#1f2937",
                  margin: 0,
                }}
              >
                Create Child Organization
              </h2>
              <p
                style={{
                  fontSize: "13px",
                  color: "#6b7280",
                  margin: 0,
                }}
              >
                Add a new organization to your hierarchy
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: "8px",
              border: "none",
              backgroundColor: "transparent",
              borderRadius: "8px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <X size={20} color="#6b7280" />
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit}>
          <div
            style={{
              padding: "24px",
              maxHeight: "calc(90vh - 180px)",
              overflowY: "auto",
            }}
          >
            {/* Organization Name */}
            <div style={{ marginBottom: "20px" }}>
              <label style={labelStyle}>
                Organization Name <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <input
                type="text"
                name="orgName"
                value={formData.orgName}
                onChange={handleInputChange}
                placeholder="Enter organization name"
                style={{
                  ...inputStyle,
                  borderColor: errors.orgName ? "#dc2626" : "#e5e7eb",
                }}
                onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                onBlur={(e) => {
                  e.target.style.borderColor = errors.orgName
                    ? "#dc2626"
                    : "#e5e7eb";
                  e.target.style.boxShadow = "none";
                }}
              />
              {errors.orgName && (
                <div style={errorStyle}>
                  <AlertCircle size={12} />
                  {errors.orgName}
                </div>
              )}
            </div>

            {/* Two Column Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
                marginBottom: "20px",
              }}
            >
              {/* Organization Type */}
              <div>
                <label style={labelStyle}>Organization Type</label>
                <select
                  name="orgType"
                  value={formData.orgType}
                  onChange={handleInputChange}
                  style={{
                    ...inputStyle,
                    cursor: "pointer",
                  }}
                >
                  <option value="">Select type</option>
                  {orgTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Parent Organization */}
              <div>
                <label style={labelStyle}>Parent Organization</label>
                <select
                  name="parentOrgId"
                  value={formData.parentOrgId}
                  onChange={handleInputChange}
                  style={{
                    ...inputStyle,
                    cursor: "pointer",
                  }}
                >
                  <option value="">Current Organization (Default)</option>
                  {accessibleOrgs.map((org) => (
                    <option key={org.org_id} value={org.org_id}>
                      {"—".repeat(org.hierarchy_level || 0)} {org.org_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Description */}
            <div style={{ marginBottom: "20px" }}>
              <label style={labelStyle}>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Brief description of the organization"
                rows={3}
                style={{
                  ...inputStyle,
                  resize: "vertical",
                  minHeight: "80px",
                }}
                onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e5e7eb";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>

            {/* Contact Info Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
                marginBottom: "20px",
              }}
            >
              {/* Email */}
              <div>
                <label style={labelStyle}>
                  <Mail size={14} style={{ marginRight: "6px" }} />
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="org@example.com"
                  style={{
                    ...inputStyle,
                    borderColor: errors.email ? "#dc2626" : "#e5e7eb",
                  }}
                  onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                  onBlur={(e) => {
                    e.target.style.borderColor = errors.email
                      ? "#dc2626"
                      : "#e5e7eb";
                    e.target.style.boxShadow = "none";
                  }}
                />
                {errors.email && (
                  <div style={errorStyle}>
                    <AlertCircle size={12} />
                    {errors.email}
                  </div>
                )}
              </div>

              {/* Mobile */}
              <div>
                <label style={labelStyle}>
                  <Phone size={14} style={{ marginRight: "6px" }} />
                  Mobile
                </label>
                <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  placeholder="9876543210"
                  maxLength={10}
                  style={{
                    ...inputStyle,
                    borderColor: errors.mobile ? "#dc2626" : "#e5e7eb",
                  }}
                  onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                  onBlur={(e) => {
                    e.target.style.borderColor = errors.mobile
                      ? "#dc2626"
                      : "#e5e7eb";
                    e.target.style.boxShadow = "none";
                  }}
                />
                {errors.mobile && (
                  <div style={errorStyle}>
                    <AlertCircle size={12} />
                    {errors.mobile}
                  </div>
                )}
              </div>
            </div>

            {/* Invite Admin Section */}
            <div
              style={{
                backgroundColor: "#f9fafb",
                borderRadius: "12px",
                padding: "16px",
                border: "1px solid #e5e7eb",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: inviteAdmin ? "16px" : 0,
                }}
              >
                <div>
                  <h4
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#1f2937",
                      margin: 0,
                    }}
                  >
                    Invite Organization Admin
                  </h4>
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      margin: "2px 0 0 0",
                    }}
                  >
                    Optionally invite an admin for this organization
                  </p>
                </div>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={inviteAdmin}
                    onChange={(e) => setInviteAdmin(e.target.checked)}
                    style={{
                      width: "18px",
                      height: "18px",
                      accentColor: "#ff7a00",
                      cursor: "pointer",
                    }}
                  />
                </label>
              </div>

              {inviteAdmin && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "12px",
                  }}
                >
                  <div>
                    <label style={{ ...labelStyle, fontSize: "12px" }}>
                      Admin Name <span style={{ color: "#dc2626" }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="adminName"
                      value={formData.adminName}
                      onChange={handleInputChange}
                      placeholder="Admin name"
                      style={{
                        ...inputStyle,
                        padding: "10px 12px",
                        fontSize: "13px",
                        borderColor: errors.adminName ? "#dc2626" : "#e5e7eb",
                      }}
                    />
                    {errors.adminName && (
                      <div style={errorStyle}>
                        <AlertCircle size={12} />
                        {errors.adminName}
                      </div>
                    )}
                  </div>
                  <div>
                    <label style={{ ...labelStyle, fontSize: "12px" }}>
                      Admin Email <span style={{ color: "#dc2626" }}>*</span>
                    </label>
                    <input
                      type="email"
                      name="adminEmail"
                      value={formData.adminEmail}
                      onChange={handleInputChange}
                      placeholder="admin@example.com"
                      style={{
                        ...inputStyle,
                        padding: "10px 12px",
                        fontSize: "13px",
                        borderColor: errors.adminEmail ? "#dc2626" : "#e5e7eb",
                      }}
                    />
                    {errors.adminEmail && (
                      <div style={errorStyle}>
                        <AlertCircle size={12} />
                        {errors.adminEmail}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: "16px 24px",
              borderTop: "1px solid #e5e7eb",
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              backgroundColor: "#f9fafb",
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                padding: "12px 24px",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                backgroundColor: "#fff",
                fontSize: "14px",
                fontWeight: "600",
                color: "#374151",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: "12px 24px",
                border: "none",
                borderRadius: "8px",
                backgroundColor: isSubmitting ? "#fbbf24" : "#ff7a00",
                fontSize: "14px",
                fontWeight: "600",
                color: "#fff",
                cursor: isSubmitting ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s ease",
                boxShadow: "0 2px 8px rgba(255, 122, 0, 0.3)",
              }}
            >
              {isSubmitting ? (
                <>
                  <Loader size={16} className="spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircle size={16} />
                  Create Organization
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .spin {
            animation: spin 1s linear infinite;
          }
        `}
      </style>
    </div>
  );
};

export default AddChildOrganization;
